import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, MapPin } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Motion";

/**
 * Reusable project detail template — content comes from CMS
 * (Admin content key "projects"). Renders a graceful "coming soon"
 * state until real project write-ups are added. Nothing is invented.
 */
export default function ProjectDetail() {
  const { id } = useParams();
  const [projects, setProjects] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/projects`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setProjects(d && Array.isArray(d.items) ? d.items : []))
      .catch(() => setProjects([]));
  }, []);

  const project = (projects || []).find((p) => p.id === id || p.slug === id);

  if (projects === null) return null;

  if (!project) {
    return (
      <div data-testid="project-detail-empty" className="pt-28 bg-white min-h-[60vh]">
        <section className="px-6 md:px-12 lg:px-20 py-20 max-w-2xl">
          <h1 className="font-display text-4xl tracking-tight font-medium text-[#111]">
            Project stories are coming soon.
          </h1>
          <p className="mt-4 text-[#4A4A4A] leading-relaxed">
            We're preparing detailed write-ups of recent Zarco's Decks projects. In the meantime,
            browse the gallery or ask us about a project like yours.
          </p>
          <div className="mt-8 flex gap-4">
            <Link to="/gallery" data-testid="project-empty-gallery-btn" className="border-2 border-[#111] px-6 py-3 text-sm font-semibold hover:bg-[#111] hover:text-white transition-colors inline-flex items-center gap-2">
              <ArrowLeft size={15} /> Back to Gallery
            </Link>
            <Link to="/contact" data-testid="project-empty-estimate-btn" className="bg-[#111] text-white px-6 py-3 text-sm font-semibold hover:bg-[#99582A] transition-colors">
              Get Your Free Estimate
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const facts = [
    ["Project City", project.city],
    ["Project Type", project.project_type],
    ["Decking Material", project.material],
    ["Color", project.color],
    ["Railing", project.railing],
    ["Stairs", project.stairs],
    ["Covered / Screened", project.covered],
  ].filter(([, v]) => v);

  return (
    <div data-testid="project-detail-page" className="pt-28 bg-white">
      <Seo
        title={`${project.title} | Zarco's Decks Project`}
        description={project.description ? project.description.slice(0, 155) : `A Zarco's Decks & Outdoor Living project${project.city ? ` in ${project.city}` : ""}.`}
      />
      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-24">
        <Reveal>
          {project.city && (
            <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold inline-flex items-center gap-2">
              <MapPin size={13} /> {project.city}
            </p>
          )}
          <h1 className="font-display text-4xl md:text-6xl tracking-tighter font-medium mt-4 text-[#111]">
            {project.title}
          </h1>
        </Reveal>

        {facts.length > 0 && (
          <Reveal delay={0.08} className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-px bg-[#E7E2D9] border border-[#E7E2D9]">
            {facts.map(([k, v]) => (
              <div key={k} className="bg-white p-5">
                <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#99582A]">{k}</p>
                <p className="mt-2 font-display text-lg tracking-tight text-[#111]">{v}</p>
              </div>
            ))}
          </Reveal>
        )}

        {project.description && (
          <Reveal delay={0.12} className="mt-10 max-w-3xl">
            <p className="text-[#4A4A4A] leading-relaxed text-base md:text-lg">{project.description}</p>
          </Reveal>
        )}

        {Array.isArray(project.photos) && project.photos.length > 0 && (
          <div className="mt-12 grid md:grid-cols-2 gap-4">
            {project.photos.map((src, i) => (
              <Reveal key={src} delay={i * 0.06} className={i === 0 ? "md:col-span-2" : ""}>
                <div className="img-hover img-spotlight overflow-hidden bg-[#E7E2D9] aspect-[16/10]">
                  <img
                    src={src}
                    alt={`${project.title} — photo ${i + 1}`}
                    loading={i === 0 ? "eager" : "lazy"}
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        )}

        <Reveal className="mt-14 bg-[#2A2A2A] text-white p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-2xl md:text-3xl tracking-tight font-medium">
              Want something like this?
            </h2>
            <p className="mt-2 text-white/80">Free on-site estimate, clear written proposal.</p>
          </div>
          <Link
            to="/contact"
            data-testid="project-detail-estimate-btn"
            className="group shrink-0 bg-[#C27A3E] hover:bg-white hover:text-[#111] transition-colors px-8 py-4 font-semibold tracking-wide inline-flex items-center gap-3"
          >
            Get Your Free Estimate <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
